// The plugin half of the shared Streamdown configuration — see
// `streamdown-config.ts` for why it sits in the neutral UI zone.
// `MermaidErrorComponent` travelled with it: it is this config's own
// `errorComponent`, and leaving it behind would have replaced four quarantine
// pairs with one.
import { code } from '@streamdown/code';
import { mermaid } from '@streamdown/mermaid';
import type { MermaidOptions, StreamdownProps } from 'streamdown';
import { MermaidErrorComponent } from './MermaidErrorComponent';

/**
 * Shiki theme tuple for syntax highlighting in code blocks.
 * Format: [lightTheme, darkTheme]
 * Uses GitHub themes for good contrast with our design system.
 */
export const shikiTheme: StreamdownProps['shikiTheme'] = [
  'github-light-default',
  'github-dark-default',
];

/**
 * Streamdown plugins configuration.
 * - code: Enables Shiki-powered syntax highlighting with lazy loading
 * - mermaid: Enables Mermaid diagram rendering with lazy loading
 */
export const streamdownPlugins = {
  code,
  mermaid,
} as StreamdownProps['plugins'];

/**
 * Mermaid diagram configuration.
 *
 * htmlLabels: false — uses native SVG <text> elements instead of HTML
 * inside <foreignObject>. This fixes a known Mermaid bug where
 * foreignObject width/height measurement is inaccurate, causing node
 * text to be clipped by the box boundaries.
 * See: https://github.com/mermaid-js/mermaid/issues/5785
 *
 * themeVariables.fontSize / fontFamily — bumps the diagram's natural font
 * size and replaces `@streamdown/mermaid`'s very-wide `monospace` default
 * with a normal sans-serif. This keeps node text readable after the diagram
 * has been shrunk to fit the chat panel and after the user maxes out
 * Streamdown's pan/zoom (Streamdown bakes `maxZoom: 3` into its bundle and
 * does not expose it as a prop, so the only lever we have to keep text
 * legible at the smallest effective scale is to make the natural font
 * larger). Reported by Christopher in EVI-1158 (#5).
 *
 * flowchart.padding — small bump so the larger labels still have breathing
 * room inside their node boxes.
 *
 * themeVariables.xyChart.plotColorPalette — Mermaid's default theme starts the
 * xychart palette at #ECECFF, so a single-series line chart draws near-white
 * on white (HEI2-199). The palette is Heidi primitives: skies-700 (the
 * `text-active` blue), tangerine-10, brandAccent-500, crimson-11 and
 * sunlight-800, each at 3:1 or better against white and separable under
 * colour-vision deficiency. Mermaid needs literal colours, since it computes
 * shades from them, and the download and fullscreen views re-render from this
 * config, so CSS cannot recolour the series.
 *
 * errorComponent — Replaces Streamdown's default red "Mermaid Error: ..."
 * panel with a streaming-aware component. While the surrounding markdown
 * block is still streaming (see {@link MermaidStreamingContext}), transient
 * parse errors are swallowed (we render nothing) and the diagram pops in
 * once the block parses cleanly, fixing the flicker described in EVI-1158.
 */
export const mermaidConfig: MermaidOptions = {
  config: {
    themeVariables: {
      fontSize: '20px',
      fontFamily:
        'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      xyChart: {
        plotColorPalette: '#2255C3, #EF5F00, #8B5CF6, #CB1D63, #666100',
      },
    },
    flowchart: {
      htmlLabels: false,
      padding: 16,
    },
  },
  errorComponent: MermaidErrorComponent,
};
